import React, { useCallback } from 'react'
import {
  ThirdPartyIcon,
  ThirdPartiesType,
  Box,
  Flex,
  Text,
  DotsLoader,
  Radio,
  TextField,
} from '@chilipiper/design-system'
import { useTranslations } from '@chilipiper/locale'
import { WaitingComponent } from '@chilipiper/meetings-components/src/waiting-component'

const TextArea = React.lazy(() => import('@chilipiper/slate-textarea'))

import { StyledBlock } from './styles'

interface Props {
  checked: boolean
  description?: string
  divider: boolean
  icon: ThirdPartiesType
  input?: {
    label: string
    onChange: (value: string) => void
    placeholder: string
    type: 'text' | 'textarea'
    value: string
  }
  name: string
  onClick: (location: string) => void
}

const RichTextArea = WaitingComponent(
  TextArea,
  <DotsLoader color='neutral-dark-32' height='100%' mt={2} />
)

export const Location = ({ name, icon, divider, onClick, checked, description, input }: Props) => {
  const onRichTextAreaChange = useCallback((val: string) => input?.onChange(val), [input])
  const [requiredText] = useTranslations(['required'])
  const onTextAreaChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => input?.onChange(e.currentTarget.value),
    [input]
  )

  return (
    <StyledBlock
      flexDirection='column'
      alignItems='center'
      cursor='pointer'
      boxShadow='border/list-item-unselected'
      width='100%'
      data-test-id={`Location-container-${name}`}
    >
      <Box w='100%'>
        <Radio
          name={`location:${name}`}
          id={`location:${name}`}
          data-test-id={`Location-${name}`}
          label={name}
          icon={<ThirdPartyIcon name={icon} />}
          separator={divider}
          checked={checked}
          description={description}
          onChange={() => onClick(name)}
        />
      </Box>
      {checked && input && (
        <Flex flexDirection='column' w='100%' p={4} pt={!input.value ? 0 : 4} bg='white'>
          {!input.value ? (
            <Text textAlign='end' py='1px' textStyle='body-monoline' color='neutral-dark-48'>
              {requiredText}
            </Text>
          ) : null}
          {input.type === 'text' ? (
            <TextField
              label={input.label}
              placeholder={input.placeholder}
              onChange={onTextAreaChange}
              value={input.value}
            />
          ) : (
            <RichTextArea
              label={input.label}
              invokeImmediately
              placeholder={input.placeholder}
              onChange={onRichTextAreaChange}
              value={input.value ?? ''}
              data-test-id={`Location-textarea-${name}`}
            />
          )}
        </Flex>
      )}
    </StyledBlock>
  )
}
